import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiType, FiAlignLeft, FiSquare, FiImage, FiVideo, FiMail, FiStar, FiMusic } from 'react-icons/fi';
import { MdOutlineSpaceBar, MdOutlineHorizontalRule } from 'react-icons/md';
import { BiColumns } from 'react-icons/bi';
import { BsGrid } from 'react-icons/bs';

const categories = [
  {
    name: 'BASIC',
    items: [
      { id: 'heading', icon: FiType, label: 'Heading' },
      { id: 'text', icon: FiAlignLeft, label: 'Text' },
      { id: 'button', icon: FiSquare, label: 'Button' },
      { id: 'container', icon: BiColumns, label: 'Container' },
      { id: 'spacer', icon: MdOutlineSpaceBar, label: 'Spacer' },
      { id: 'separator', icon: MdOutlineHorizontalRule, label: 'Separator' },
    ],
  },
  {
    name: 'FORMS',
    items: [
      { id: 'contact-form', icon: FiMail, label: 'Contact Form' },
    ],
  },
  {
    name: 'MEDIA',
    items: [
      { id: 'image', icon: FiImage, label: 'Image' },
      { id: 'video', icon: FiVideo, label: 'Video' },
      { id: 'artist', icon: FiMusic, label: 'Music' },
    ],
  },
  {
    name: 'STORE',
    items: [
      { id: 'products', icon: BsGrid, label: 'Products' },
    ],
  },
  {
    name: 'SOCIAL PROOF',
    items: [
      { id: 'reviews', icon: FiStar, label: 'Reviews' },
    ],
  },
];

const ElementsPanel = ({ isOpen, panelWidth = 320 }) => {
  const handleDragStart = (e, item) => {
    e.dataTransfer.setData('widgetType', item.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const gridCols =
    panelWidth < 260
      ? 'grid-cols-2'
      : panelWidth > 420
      ? 'grid-cols-4'
      : 'grid-cols-3';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="bg-[#0a0a0a] border-r border-[#1e2228] h-full overflow-y-auto w-full custom-scrollbar"
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-black text-xs uppercase tracking-[0.2em] text-gray-400">
                Add Widget
              </h2>
            </div>

            <div className="space-y-7">
              {categories.map((category, idx) => (
                <div key={idx}>
                  {/* Category Divider */}
                  <div className="flex items-center justify-center mb-4 relative">
                    <div className="absolute w-full h-[1px] bg-[#1e2228]" />
                    <span className="bg-[#0a0a0a] px-3 text-[10px] font-black text-gray-600 uppercase relative z-10 tracking-[0.2em]">
                      {category.name}
                    </span>
                  </div>

                  {/* Widget Grid */}
                  <div className={`grid ${gridCols} gap-2`}>
                    {category.items.map((item, itemIdx) => (
                      <div
                        key={itemIdx}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        className="flex flex-col items-center justify-center p-3 border border-[#1e2228] rounded-xl hover:border-[#c8f542]/40 hover:bg-[#c8f542]/5 cursor-grab active:cursor-grabbing transition-all bg-[#111318] group"
                      >
                        <SafeIcon
                          icon={item.icon}
                          className="w-5 h-5 text-gray-500 group-hover:text-[#c8f542] mb-2 transition-colors"
                        />
                        <span className="text-[9px] text-gray-500 group-hover:text-gray-300 text-center uppercase tracking-wider leading-tight transition-colors font-bold">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Pro Tip */}
            <div className="mt-8 p-4 bg-[#111318] rounded-xl border border-[#1e2228]">
              <p className="text-[10px] text-gray-500 font-medium leading-relaxed">
                <span className="text-[#c8f542] font-black">✦ Tip:</span>{' '}
                Drag any widget and drop it onto the canvas to start building.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ElementsPanel;